import { graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';
import { locations } from '../../../utils/tracking/shared/favoritesTracking';

// types
import { MouseEvent } from 'react';
import { PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer$data } from './__generated__/PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer.graphql';
import { PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer$data } from './__generated__/PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer.graphql';

export type FavoritesLocation = keyof typeof locations;

export type CarouselItemsDataKey = Extract<
    | keyof PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer$data
    | keyof PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer$data,
    'sellerItems' | 'ymalItems'
>;

export type Item =
    | NonNullable<
          NonNullable<
              NonNullable<
                  PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer$data['ymalItems']
              >['edges']
          >[number]
      >['node']
    | NonNullable<
          NonNullable<
              NonNullable<
                  PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer$data['sellerItems']
              >['edges']
          >[number]
      >['node'];

type TableMetadataArgs =
    | {
          itemId?: string | null;
          seller?: {
              isAccessible?: boolean | null;
              serviceId?: string | null;
          } | null;
      }
    | undefined;

type TableMetadataReturn = {
    isVisible: boolean;
    trackingList: string;
    viewAllLinkData: {
        path: string;
        isLinkable: boolean;
        onClick: (e: MouseEvent) => void;
    } | null;
    favoritesLocation: FavoritesLocation;
};

export const PdpRespSharedItemsRefetchCarouselConfig = [
    {
        title: (
            <FormattedMessage
                defaultMessage="More From This Seller"
                id="PdpRespSharedItemsRefetchCarouselConfig.moreFromThisSeller"
            />
        ),
        type: 'more-from-seller-grid',
        dataTn: 'pdp-more-from-this-seller-grid',
        key: 'sellerItems' as CarouselItemsDataKey,
        getTableMetadata: (args: TableMetadataArgs): TableMetadataReturn => {
            const { seller, itemId } = args || {};
            const { serviceId, isAccessible } = seller || {};
            const viewAllLinkData =
                serviceId && itemId
                    ? {
                          isLinkable: false,
                          onClick: (event: MouseEvent): void => {
                              trackEvent(
                                  {
                                      action: 'click to seller page',
                                      category: 'dealer click',
                                      label: 'PDP',
                                  },
                                  event || null
                              );
                          },
                          dataTn: 'pdp-product-tile-carousel-view-all-link',
                          path: `/more-from-seller/?itemPk=${itemId}&sellerPk=${serviceId}`,
                      }
                    : null;
            return {
                viewAllLinkData,
                isVisible: !!isAccessible,
                trackingList: 'pdp more from seller',
                favoritesLocation: locations.SELLER_GRID as FavoritesLocation,
            };
        },
        query: graphql`
            fragment PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer on Viewer
            @refetchable(queryName: "PdpRespSharedItemsCarouselSellerYMALRefetchQuery")
            @argumentDefinitions(
                count: { type: "Int", defaultValue: 6 }
                cursor: { type: "String" }
            ) {
                ...PdpRespSharedCarouselItemTile_viewer
                sellerItems: moreLikeThisItem(
                    first: $count
                    after: $cursor
                    seller: INCLUDE
                    itemId: $itemId
                ) @connection(key: "PdpRespSharedSellerItemCarousel_sellerItems", filters: []) {
                    edges {
                        node {
                            title
                            serviceId
                            browseUrl
                            categoryCode
                            categoryPath
                            ecommerceTrackingParams
                            ...PdpRespSharedCarouselItemTile_item
                        }
                    }
                }
            }
        `,
    },

    {
        title: (
            <FormattedMessage
                defaultMessage="You May Also Like"
                id="PdpRespSharedItemsRefetchCarouselConfig.youMayAlsoLike"
            />
        ),
        type: 'similar-recommendation-grid',
        dataTn: 'pdp-similar-recommendation-grid',
        key: 'ymalItems' as CarouselItemsDataKey,
        getTableMetadata: (args: TableMetadataArgs): TableMetadataReturn => {
            const { itemId } = args || {};
            const viewAllLinkData = itemId
                ? {
                      isLinkable: false,
                      dataTn: 'pdp-similar-recommendation-grid-view-all-link',
                      path: `/similar/?item=${itemId}`,
                      onClick: (event: MouseEvent): void => {
                          trackEvent(
                              {
                                  category: 'product',
                                  action: 'click on ymal view all',
                                  label: 'PDP',
                              },
                              event || null
                          );
                      },
                  }
                : null;
            return {
                isVisible: true,
                viewAllLinkData,
                trackingList: 'pdp you may also like',
                favoritesLocation: locations.SIMILAR_ITEM_GRID as FavoritesLocation,
            };
        },
        query: graphql`
            fragment PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer on Viewer
            @refetchable(queryName: "PdpRespSharedItemsCarouselYMALRefetchQuery")
            @argumentDefinitions(
                count: { type: "Int", defaultValue: 6 }
                cursor: { type: "String" }
            ) {
                ...PdpRespSharedCarouselItemTile_viewer
                ymalItems: moreLikeThisItem(
                    first: $count
                    after: $cursor
                    seller: EXCLUDE
                    itemId: $itemId
                    excludeAuctionItems: true
                ) @connection(key: "PdpRespSharedYMALItemCarousel_ymalItems", filters: []) {
                    edges {
                        node {
                            title
                            serviceId
                            browseUrl
                            categoryCode
                            categoryPath
                            ecommerceTrackingParams
                            ...PdpRespSharedCarouselItemTile_item
                        }
                    }
                }
            }
        `,
    },
];
